import { GResponse } from "../../models/common.ts";

import type {
    OpenItem,
    OpenItemComment,
    OpenItemGroupedStats,
    OpenItemPostPayload,
    OpenItemPostPayloadResolve,
    OpenItemPostPayloadUnResolve,
} from "../../models/contador.ts";
import { fetchWithAuth } from "../../config/axios.ts";
import { fetchFromContador } from "./contador-fetch.ts";

export async function getOpenItems(wuId: string) {
    const url = `/api/v1/open_items?filter[reported_in_ticket_id]=${wuId}`;
    const data = await fetchFromContador<GResponse<OpenItem>>(url);
    return data.items;
}

export async function getGroupedOpenItems(wuId?: string) {
    const url = wuId
        ? `/api/v1/open_items/grouped_stats?filter[reported_in_ticket_id]=${wuId}`
        : "/api/v1/open_items/grouped_stats";

    return await fetchFromContador<OpenItemGroupedStats[]>(url);
}

export async function getOpenItem(id: string) {
    const url = `/api/v1/open_items/${id}`;
    return await fetchFromContador<OpenItem>(url);
}

export async function getFullOpenItem(id: string): Promise<OpenItem> {
    const url = `/api/v1/open_items/${id}?include=Comments&include=Changelog&include=Property&include=BankAccount`;
    return await fetchFromContador<OpenItem>(url);
}

/**
 * Create or update an open item.
 */
export async function postOpenItem(py: OpenItemPostPayload) {
    const url = `/api/v1/open_items`;
    const data = await fetchFromContador<OpenItem[]>(url, {
        method: "POST",
        body: JSON.stringify({
            data: [
                {
                    ...py,
                },
            ],
        }),
    });
    return data[0];
}

export async function deleteOpenItem(id: string) {
    const url = `/api/v1/open_items/${id}`;
    return await fetchFromContador<void>(url, {
        method: "DELETE",
    });
}

export async function resolveOpenItem(py: OpenItemPostPayloadResolve) {
    const url = `/api/v1/open_items`;
    const data = await fetchFromContador<OpenItem[]>(url, {
        method: "POST",
        body: JSON.stringify({
            data: [
                {
                    ...py,
                },
            ],
        }),
    });
    return data[0];
}

export async function unResolveOpenItem(py: OpenItemPostPayloadUnResolve) {
    const url = `/api/v1/open_items`;
    const data = await fetchFromContador<OpenItem[]>(url, {
        method: "POST",
        body: JSON.stringify({
            data: [
                {
                    ...py,
                },
            ],
        }),
    });
    return data[0];
}

export async function getAllOpenItemsForClient(clientId: string, propertiesIds: string[], showResolved: boolean = false) {
    let url = `/api/v1/open_items?filter[customer_id]=${clientId}&include=Property&include=BankAccount&include=Comments`;
    if (propertiesIds.length > 0) {
        for (const propId of propertiesIds) {
            url += `&filter[property_id]=${propId}`;
        }
        url += `&filter[property_id]=null`;
    }
    url += `&ticketsStatus=done`;
    url += `&page[limit]=999`;
    if (!showResolved) {
        url += `&filter[status]=open`;
    }
    const data = await fetchFromContador<GResponse<OpenItem>>(url);
    return data.items;
}

export async function getOpenItemsForClient(clientId: string) {
    const url = `/api/v1/open_items?filter[customer_id]=${clientId}&filter[status]=open&include=Property&include=BankAccount&include=Comments`;
    const data = await fetchFromContador<GResponse<OpenItem>>(url);
    return data.items;
}

/**
 * Create a comment for an open item.
 */
export async function postOpenItemComment(payload: {
    open_item_id: string;
    content: string;
    author: string;
}) {
    const url = `/api/v1/open_item_comments`;
    const data = await fetchFromContador<OpenItemComment>(url, {
        method: "POST",
        body: JSON.stringify(payload),
    });
    return data;
}

/**
 * Create a comment for an open item with attachments.
 */
export async function postOpenItemCommentWithAttachments(payload: {
    open_item_id: string;
    content: string;
    author: string;
    attachments: File[];
}) {
    const url = `/contador/api/v1/open_item_comments`;

    const formData = new FormData();

    payload.attachments.forEach((attachment) => {
        formData.append("attachments", attachment);
    });

    formData.append(
        "body",
        JSON.stringify({
            open_item_id: payload.open_item_id,
            content: payload.content,
            author: payload.author,
        }),
    );

    const response = await fetchWithAuth.request({
        url,
        method: "POST",
        data: formData,
    });

    return response.data;
}

/**
 * Update an open item comment.
 */
export async function updateOpenItemComment(
    id: string,
    payload: {
        content: string;
        author: string;
        open_item_id: string;
    },
) {
    const url = `/api/v1/open_item_comments/${id}`;
    const data = await fetchFromContador<OpenItemComment>(url, {
        method: "PUT",
        body: JSON.stringify(payload),
    });
    return data;
}

/**
 * Delete an open item comment.
 */
export async function deleteOpenItemComment(id: string) {
    const url = `/api/v1/open_item_comments/${id}`;
    return await fetchFromContador<void>(url, {
        method: "DELETE",
    });
}

export async function bulkResolveOpenItems(py: OpenItemPostPayloadResolve[]) {
    const url = `/api/v1/open_items`;
    return await fetchFromContador<OpenItem[]>(url, {
        method: "POST",
        body: JSON.stringify({
            data: py,
        }),
    });
}

export async function bulkUnResolveOpenItems(py: OpenItemPostPayloadUnResolve[]) {
    const url = `/api/v1/open_items`;
    return await fetchFromContador<OpenItem[]>(url, {
        method: "POST",
        body: JSON.stringify({
            data: py,
        }),
    });
}
